import React, { ReactElement, useContext } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Divider,
  Grid,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  ArrowBack as ArrowBackIcon,
  CreditCard as CreditCardIcon,
  ReceiptLong as ReceiptLongIcon,
} from "@mui/icons-material";
import { useTextDomainContext } from "@/app/providers";
import { usePageTitle } from "@/hooks";
import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { enqueueSnackbar } from "notistack";

const CREATE_STRIPE_CHECKOUT_SESSION = gql`
  mutation CreateStripeCheckoutSession(
    $setupFee: Float
    $platformLicense: Float
    $energyTypes: Float
    $users: Float
    $companies: Float
    $countries: Float
    $locations: Float
    $totalRecurring: Float
    $grandTotal: Float!
    $currency: String!
    $billingCycle: String!
    $setupChecked: Boolean
    $energyElectricity: Boolean
    $energyGas: Boolean
    $usersQty: Int
    $companiesQty: Int
    $countriesQty: Int
    $locationsQty: Int
    $successUrl: String
    $cancelUrl: String
  ) {
    createStripeCheckoutSession(
      setupFee: $setupFee
      platformLicense: $platformLicense
      energyTypes: $energyTypes
      users: $users
      companies: $companies
      countries: $countries
      locations: $locations
      totalRecurring: $totalRecurring
      grandTotal: $grandTotal
      currency: $currency
      billingCycle: $billingCycle
      setupChecked: $setupChecked
      energyElectricity: $energyElectricity
      energyGas: $energyGas
      usersQty: $usersQty
      companiesQty: $companiesQty
      countriesQty: $countriesQty
      locationsQty: $locationsQty
      successUrl: $successUrl
      cancelUrl: $cancelUrl
    ) {
      checkoutUrl
      sessionId
      success
      message
    }
  }
`;

interface CheckoutState {
  setupChecked: boolean;
  energyElectricity: boolean;
  energyGas: boolean;
  usersQty: number;
  companiesQty: number;
  countriesQty: number;
  locationsQty: number;
  currency: "EUR" | "DKK";
  billingCycle: "monthly" | "yearly";
  grandTotal: number;
  setupSubtotal: number;
  platformSubtotal: number;
  energySubtotal: number;
  usersSubtotal: number;
  companiesSubtotal: number;
  countriesSubtotal: number;
  locationsSubtotal: number;
  totalRecurring: number;
}

export const Checkout = (): ReactElement => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  usePageTitle(gettext("Checkout Invoice"));

  // Retrieve state or use safe default values
  const state = (location.state as CheckoutState) || {
    setupChecked: true,
    energyElectricity: true,
    energyGas: false,
    usersQty: 1,
    companiesQty: 1,
    countriesQty: 1,
    locationsQty: 1,
    currency: "EUR",
    billingCycle: "monthly",
    grandTotal: 1475,
    setupSubtotal: 999,
    platformSubtotal: 129,
    energySubtotal: 99,
    usersSubtotal: 49,
    companiesSubtotal: 79,
    countriesSubtotal: 120,
    locationsSubtotal: 120,
    totalRecurring: 476,
  };

  const {
    setupChecked,
    energyElectricity,
    energyGas,
    usersQty,
    companiesQty,
    countriesQty,
    locationsQty,
    currency,
    billingCycle,
    grandTotal,
    setupSubtotal,
    platformSubtotal,
    energySubtotal,
    usersSubtotal,
    companiesSubtotal,
    countriesSubtotal,
    locationsSubtotal,
    totalRecurring,
  } = state;

  const [createSession, { loading }] = useMutation(
    CREATE_STRIPE_CHECKOUT_SESSION,
    {
      onCompleted: (data: any) => {
        if (
          data?.createStripeCheckoutSession?.success &&
          data?.createStripeCheckoutSession?.checkoutUrl
        ) {
          // Redirect directly to Stripe Checkout website
          window.location.href = data.createStripeCheckoutSession.checkoutUrl;
        } else {
          enqueueSnackbar(
            data?.createStripeCheckoutSession?.message ||
              gettext("Stripe payment session creation failed."),
            { variant: "error" },
          );
        }
      },
      onError: (err) => {
        enqueueSnackbar(err.message, { variant: "error" });
      },
    },
  );

  const formatPrice = (val: number) => {
    const formatted = new Intl.NumberFormat(
      currency === "DKK" ? "da-DK" : "en-US",
      {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      },
    ).format(val);
    return currency === "EUR" ? `€${formatted}` : `${formatted} kr.`;
  };

  const handleCheckout = () => {
    const successUrl = `${window.location.origin}/user-account/checkout/success`;
    const cancelUrl = `${window.location.origin}/user-account/checkout/failure`;

    createSession({
      variables: {
        setupFee: setupSubtotal,
        platformLicense: platformSubtotal,
        energyTypes: energySubtotal,
        users: usersSubtotal,
        companies: companiesSubtotal,
        countries: countriesSubtotal,
        locations: locationsSubtotal,
        totalRecurring: totalRecurring,
        grandTotal: grandTotal,
        currency: currency,
        billingCycle: billingCycle,
        setupChecked: setupChecked,
        energyElectricity: energyElectricity,
        energyGas: energyGas,
        usersQty: usersQty,
        companiesQty: companiesQty,
        countriesQty: countriesQty,
        locationsQty: locationsQty,
        successUrl: successUrl,
        cancelUrl: cancelUrl,
      },
    });
  };

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 2, md: 3 } }}>
      {/* Header & Back Action */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <IconButton
          onClick={() => navigate("/user-account/services")}
          sx={{ mr: 1, color: "text.primary" }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>
          {gettext("Complete Payment")}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Invoice Itemized Details */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Card
            sx={{
              borderRadius: 4,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.05)",
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", mb: 3, gap: 1 }}
              >
                <ReceiptLongIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {gettext("Invoice Details")}
                </Typography>
              </Box>

              <Stack spacing={2}>
                {setupChecked && (
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {gettext("One-time Setup Fee")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {gettext("Initial platform provisioning")}
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatPrice(setupSubtotal)}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {gettext("Platform License")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {gettext("Core license status")}
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {formatPrice(platformSubtotal)}
                  </Typography>
                </Box>

                {(energyElectricity || energyGas) && (
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {gettext("Energy Types")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {energyElectricity && "Electricity"}{" "}
                        {energyElectricity && energyGas && "&"}{" "}
                        {energyGas && "Gas"}
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatPrice(energySubtotal)}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {gettext("Additional Users")} (x{usersQty})
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {gettext("Collaborative users allowed")}
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {formatPrice(usersSubtotal)}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {gettext("Subsidiaries / Companies")} (x{companiesQty})
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {gettext("Registered business companies")}
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {formatPrice(companiesSubtotal)}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {gettext("Countries")} (x{countriesQty})
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {gettext("Operational nations scope")}
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {formatPrice(countriesSubtotal)}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {gettext("Meters / Locations")} (x{locationsQty})
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {gettext("Monitored measurement points")}
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {formatPrice(locationsSubtotal)}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Grand Total & Stripe Button */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Card
            sx={{
              borderRadius: 4,
              border: "2px solid",
              borderColor: "primary.main",
              boxShadow: "0 12px 40px rgba(0, 0, 0, 0.08)",
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.primary.main, 0.06)} 100%)`,
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Typography
                variant="subtitle2"
                color="primary"
                sx={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  tracking: 1.5,
                  mb: 1,
                }}
              >
                {gettext("Checkout Invoice Summary")}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 3 }}
              >
                {gettext("Billing Period:")}{" "}
                {billingCycle === "yearly"
                  ? gettext("Yearly (12 months)")
                  : gettext("Monthly")}
              </Typography>

              <Stack spacing={2} sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography color="text.secondary">
                    {gettext("Recurring Total")}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {formatPrice(totalRecurring)}
                  </Typography>
                </Box>
                {setupChecked && (
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography color="text.secondary">
                      {gettext("Setup Fee (One-time)")}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatPrice(setupSubtotal)}
                    </Typography>
                  </Box>
                )}
                <Divider />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      {gettext("Grand Total")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {gettext("excl. VAT")}
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    color="primary"
                    sx={{ fontWeight: 900 }}
                  >
                    {formatPrice(grandTotal)}
                  </Typography>
                </Box>
              </Stack>

              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <CreditCardIcon />
                  )
                }
                onClick={handleCheckout}
                sx={{
                  py: 2,
                  borderRadius: 3,
                  fontWeight: 800,
                  fontSize: "1.05rem",
                  boxShadow: `0 12px 28px ${alpha(theme.palette.primary.main, 0.25)}`,
                  transition: "all 0.3s",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: `0 16px 36px ${alpha(theme.palette.primary.main, 0.35)}`,
                  },
                }}
              >
                {loading
                  ? gettext("Preparing stripe session...")
                  : gettext("Pay with Stripe")}
              </Button>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", textAlign: "center", mt: 2 }}
              >
                {gettext(
                  "Payments are secured by Stripe. By proceeding, you agree to our Terms.",
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
